import { FC, useState } from 'react';
import { useForm } from 'react-final-form';
import { UPLOAD_CONFIGS } from './constants';
import { useFieldValue } from 'hooks/useFormHelpers';

type Props = {
  type?: 'tag' | 'community' | 'set-video' | 'set-logo' | 'section' | 'optional-media';
  label?: string;
  previewUrl?: string;
  fileField?: string;
  previewField?: string;
  hasImageField?: string;
  inputId?: string;
  initialFileType?: 'image' | 'video';
};

const ACCEPT_MAP: Record<string, string> = {
  image: 'image/*',
  video: 'video/*',
  'image-or-video': 'image/*,video/*',
};

export const UploadFileForm: FC<Props> = ({
  type = 'tag',
  label = '',
  previewUrl,
  fileField,
  previewField,
  hasImageField,
  inputId,
  initialFileType,
}) => {
  const { change } = useForm();
  const config = UPLOAD_CONFIGS[type] || UPLOAD_CONFIGS.tag;
  const resolvedFileField = fileField || config.fileField;
  const resolvedPreviewField = previewField || config.previewField;
  const resolvedInputId = inputId || `file-${type}`;

  const formPreview = useFieldValue(resolvedPreviewField || '');
  const [localPreview, setLocalPreview] = useState<string | undefined>(undefined);
  const [localFileType, setLocalFileType] = useState<'image' | 'video' | undefined>(undefined);
  const [typeError, setTypeError] = useState<string | undefined>(undefined);

  const preview = formPreview ?? localPreview ?? previewUrl;
  const isImageOrVideo = config.displayType === 'image-or-video';

  const resolvedFileType: 'image' | 'video' | undefined = localFileType ?? initialFileType;

  const isVideoPreview =
    config.displayType === 'video' || (isImageOrVideo && resolvedFileType === 'video');

  const clearImage = () => {
    if (resolvedPreviewField) {
      change(resolvedPreviewField, undefined);
    } else {
      setLocalPreview(undefined);
    }
    if (hasImageField) {
      change(hasImageField, false);
    }
    change(resolvedFileField, undefined);
    setLocalFileType(undefined);
    setTypeError(undefined);
  };

  const onSelectFile = e => {
    if (!e.target.files || e.target.files.length === 0) {
      clearImage();
      return;
    }

    const file = e.target.files[0];
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');

    if (config.displayType === 'image' && isVideo) {
      setTypeError('Only image files are allowed.');
      e.target.value = '';
      return;
    }
    if (config.displayType === 'video' && isImage) {
      setTypeError('Only video files are allowed.');
      e.target.value = '';
      return;
    }

    setTypeError(undefined);

    if (isImageOrVideo) {
      setLocalFileType(isVideo ? 'video' : 'image');
    }

    change(resolvedFileField, file);
    if (hasImageField) {
      change(hasImageField, true);
    }

    const newPreviewUrl = URL.createObjectURL(file);
    if (resolvedPreviewField) {
      change(resolvedPreviewField, newPreviewUrl);
    } else {
      setLocalPreview(newPreviewUrl);
    }
  };

  const uploadLabel = isImageOrVideo
    ? 'Upload Image or Video'
    : `Upload ${config.displayType === 'video' ? 'Video' : 'Image'}`;

  const clearLabel = isImageOrVideo
    ? 'Clear Media'
    : `Clear ${config.displayType === 'video' ? 'Video' : 'Image'}`;

  const footerText = isImageOrVideo
    ? 'If you have any problems with your custom media on the page, please email partners@oath.vote'
    : `If you have any problems with your custom ${config.displayType === 'video' ? 'video' : 'image / logo'} on the page, please email partners@oath.vote`;

  return (
    <div className="form-group">
      <label htmlFor={resolvedInputId}>{label || config.label}</label>
      <br />
      <input
        id={resolvedInputId}
        name={resolvedInputId}
        type="file"
        onChange={onSelectFile}
        accept={ACCEPT_MAP[config.displayType] || 'image/*'}
        className="display-none form-control"
      />
      <label htmlFor={resolvedInputId} className="custom-input-file">
        {uploadLabel}
      </label>
      {typeError && <p className="error">{typeError}</p>}
      <br />
      {preview &&
        (isVideoPreview ? (
          <video id={resolvedInputId} className="preview" controls playsInline>
            <source src={preview} type="video/mp4" />
          </video>
        ) : (
          <img
            id={resolvedInputId}
            className="preview"
            src={preview}
            onError={() =>
              resolvedPreviewField
                ? change(resolvedPreviewField, undefined)
                : setLocalPreview(undefined)
            }
          />
        ))}
      <div className="category form-category">{footerText}</div>
      {preview && (
        <button type="button" className="oath-utility link-text gray padding0" onClick={clearImage}>
          {clearLabel}
        </button>
      )}
    </div>
  );
};
